'use client';

import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { useBuyerProductStore } from 'app/store/product';
import { getS3ObjectUrl, getStaticImageSource } from 'app/libs/url';
import { StandardContent } from './StandardContent';
import { AccordionContent } from './AccordionContent';
import { SSR } from './ssr';
import STYLE from './ProductDescription.module.scss';

const defaultContentMediaSource = [
    getStaticImageSource(
        getS3ObjectUrl(`/static/image-gallery/image-gallery-presentation-2.png`)
    ),
    getStaticImageSource(
        getS3ObjectUrl(`/static/image-gallery/image-gallery-presentation-3.png`)
    ),
];

const defaultContentList = [
    {
        id: '01JRYDZPN1KTYNYFMEY9H2RZ0A',
        alias: '商品介紹',
        title: '商品介紹',
        description: `<p><span style="color: #150000">微皺純色的布料以及前後寬鬆自在的版型，在領口與袖口有設計細節，讓夏季穿搭不無聊。</span></p><p><br></p><p><span style="color: #150000">商品特色</span></p><ul class="rich-text-style--bullet-list"><li class="rich-text-style--list-item"><p><span style="color: #150000">前後Ｖ領</span></p></li><li class="rich-text-style--list-item"><p><span style="color: #150000">舒適棉質</span></p></li><li class="rich-text-style--list-item"><p><span style="color: #150000">壓紋車線</span></p></li></ul><p><br></p><img src=${defaultContentMediaSource[0].src} srcset=${defaultContentMediaSource[0].srcSet} type=${defaultContentMediaSource[0].type} class="rich-text-media"><p><br></p><img src=${defaultContentMediaSource[1].src} srcset=${defaultContentMediaSource[1].srcSet} type=${defaultContentMediaSource[1].type} class="rich-text-media">`,
    },
    {
        id: '01JRYE98YZF8PWBD8V4C7FPE1X',
        alias: '洗滌建議',
        title: '洗滌建議',
        description: `<p>洗滌建議</p><ol class="rich-text-style--ordered-list"><li class="rich-text-style--list-item"><p>機洗或手洗皆可，若使用洗衣機請務必使用洗衣袋。</p></li><li class="rich-text-style--list-item"><p>鮮豔及深色衣物，首次清洗建議單獨下水。</p></li></ol>`,
    },
    {
        id: '01JRYEA3JTN1RYQW9CM73KKSX6',
        alias: '退換貨說明',
        title: '退換貨說明',
        description: `<ul class="rich-text-style--bullet-list"><li class="rich-text-style--list-item"><p>退換貨說明內容一</p></li><li class="rich-text-style--list-item"><p>退換貨說明內容二</p></li></ul>`,
    },
];

interface ProductDescriptionProps
    extends BasicShopUIPropTypes,
        BuyerLayoutProps<Product> {}

export const ProductDescription = forwardRef<HTMLDivElement, ProductDescriptionProps>(
    (props, ref) => {
        const { styleMap, isBuyer, __ssr } = props;

        const [hydrated, setHydrated] = useState(false);

        const product = useBuyerProductStore((state) => state.product);

        const contentList = isBuyer ? product?.descriptions : defaultContentList;

        const ContentComponent = useMemo(() => {
            const type =
                styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_DESCRIPTION_TEMPLATE_TYPE] ||
                'standard';

            return type === 'accordion' ? AccordionContent : StandardContent;
        }, [styleMap]);

        useEffect(() => {
            setHydrated(true);
        }, []);

        if (!hydrated && isBuyer) {
            return <SSR.StandardContent __ssr={__ssr} />;
        }

        return (
            <div className={STYLE.root} ref={ref}>
                {contentList?.map((content) => (
                    <ContentComponent
                        key={content.id}
                        title={content.title}
                        description={content.description}
                    />
                ))}
            </div>
        );
    }
);

ProductDescription.displayName = 'ProductDescription';
